import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { Menu, X } from 'lucide-react';
import { useTheme, darkTheme, lightTheme } from '../context/ThemeContext';
import ThemeToggle from './ThemeToggle';
// Import the logos for different themes from the assets directory
import vastLogoLight from '../assets/vast-new-logo.png';
import vastLogoDark from '../assets/vast-new-logo-dark.png';

const Header = () => {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [scrolled, setScrolled] = useState(false);
  const { theme } = useTheme();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) {
        setMenuOpen(false);
      }
    };

    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const routes = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Portfolio', path: '/portfolio' },
    { name: 'Resources', path: '/resources' },
    // { name: 'Testimonials', path: '/testimonials' }, // Commented out until we have testimonials to display
    { name: 'Contact', path: '/contact' },
  ];

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const isActive = (path) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  return (
    <HeaderContainer $scrolled={scrolled}>
      <HeaderContent>
        <Logo to="/">
          <LogoImage
            src={theme === 'dark' ? vastLogoDark : vastLogoLight}
            alt="VAST Logo"
            $scrolled={scrolled}
          />
        </Logo>

        <RightSection>
          {!isMobile && (
            <NavContainer>
              {routes.map((route) => (
                <NavItem
                  key={route.path}
                  to={route.path}
                  $active={isActive(route.path)}
                  $scrolled={scrolled}
                >
                  <NavText $scrolled={scrolled}>{route.name}</NavText>
                  {isActive(route.path) && (
                    <ActiveIndicator />
                  )}
                </NavItem>
              ))}
            </NavContainer>
          )}

          <ThemeToggle />

          {isMobile && (
            <MenuButton onClick={toggleMenu}>
              {menuOpen ? (
                <X size={24} />
              ) : (
                <Menu size={24} />
              )}
            </MenuButton>
          )}
        </RightSection>
      </HeaderContent>

      {isMobile && menuOpen && (
        <MobileMenu>
          {routes.map((route) => (
            <MobileNavItem
              key={route.path}
              to={route.path}
              $active={isActive(route.path)}
              onClick={() => setMenuOpen(false)}
            >
              {route.name}
              {isActive(route.path) && (
                <MobileActiveIndicator />
              )}
            </MobileNavItem>
          ))}
        </MobileMenu>
      )}
    </HeaderContainer>
  );
};

const HeaderContainer = styled.header`
  background: ${props => {
    // For dark theme, keep the existing behavior
    if (props.theme === darkTheme) {
      return props.$scrolled ? props.theme.glass : 'transparent';
    }
    // For light theme, use a color that matches the logo
    return props.$scrolled ? 'rgba(0, 133, 255, 0.85)' : 'transparent';
  }};
  backdrop-filter: ${props => props.$scrolled ? 'blur(10px)' : 'none'};
  -webkit-backdrop-filter: ${props => props.$scrolled ? 'blur(10px)' : 'none'};
  border-bottom: 1px solid ${props => {
    if (props.$scrolled) {
      return props.theme === darkTheme ? props.theme.glassBorder : 'rgba(0, 133, 255, 0.3)';
    }
    return 'transparent';
  }};
  position: sticky;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  transition: all 0.3s ease;
  padding: ${props => props.$scrolled ? '2px 0' : '5px 0'};
  box-shadow: ${props => props.$scrolled 
    ? '0 4px 20px rgba(0, 0, 0, 0.1)' 
    : 'none'};
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 var(--spacing-lg);
  max-width: var(--container-width);
  margin: 0 auto;
  position: relative;
`;

const Logo = styled(Link)`
  text-decoration: none;
  display: flex;
  align-items: center;
  position: relative;
  z-index: 2;
`;

const LogoImage = styled.img`
  height: ${props => props.$scrolled ? '50px' : '60px'};
  width: auto;
  object-fit: contain;
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.05);
  }
  
  @media (max-width: 480px) {
    height: ${props => props.$scrolled ? '40px' : '50px'};
  }
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const MenuButton = styled.button`
  background: ${props => props.theme === darkTheme 
    ? props.theme.glass 
    : 'rgba(255, 255, 255, 0.2)'
  };
  border: 1px solid ${props => props.theme === darkTheme 
    ? props.theme.glassBorder 
    : 'rgba(255, 255, 255, 0.3)'
  };
  border-radius: 50%;
  width: 40px;
  height: 40px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${props => props.theme === darkTheme ? props.theme.text : '#FFFFFF'};
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  
  &:focus {
    outline: none;
  }
`;

const NavContainer = styled.nav`
  display: flex;
  gap: 32px;
`;

const NavItem = styled(Link)`
  position: relative;
  padding: 8px 0;
  font-size: 16px;
  color: ${props => {
    // In light theme with scrolled header, use white text for better contrast
    if (props.theme === lightTheme && props.$scrolled) {
      return '#FFFFFF';
    }
    return props.theme.text;
  }};
  text-decoration: none;
  font-weight: 500;
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
`;

const NavText = styled.span`
  transition: color 0.3s ease;
  
  ${NavItem}:hover & {
    color: ${props => {
      // In light theme with scrolled header, use a lighter accent color for better contrast
      if (props.theme === lightTheme && props.$scrolled) {
        return '#FFFFFF';
      }
      return props.theme.accent;
    }};
  }
`;

const ActiveIndicator = styled.div`
  position: absolute;
  bottom: -2px;
  left: 0;
  right: 0;
  height: 3px;
  background: ${props => props.theme.accentGradient};
  border-radius: 3px;
`;

const MobileMenu = styled.nav`
  background: ${props => props.theme === darkTheme 
    ? props.theme.glass 
    : 'rgba(0, 133, 255, 0.95)'
  };
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-bottom: 1px solid ${props => props.theme === darkTheme 
    ? props.theme.glassBorder 
    : 'rgba(0, 133, 255, 0.3)'
  };
  overflow: hidden;
`;

const MobileNavItem = styled(Link)`
  display: flex;
  align-items: center;
  padding: 16px var(--spacing-lg);
  font-size: 18px;
  color: ${props => props.theme === darkTheme ? props.theme.text : '#FFFFFF'};
  text-decoration: none;
  font-weight: ${props => props.$active ? '600' : '500'};
  position: relative;
  
  &:not(:last-child) {
    border-bottom: 1px solid ${props => props.theme === darkTheme 
      ? props.theme.glassBorder 
      : 'rgba(255, 255, 255, 0.2)'
    };
  }
`;

const MobileActiveIndicator = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: ${props => props.theme.accentGradient};
`;

export default Header;
